import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw } from 'react-feather';
import { toast } from 'react-toastify';
import { telephonyApi } from '../../../Telephony/telephonyApi';
import { Page, PageHeader, Card, Button, Table, Loader, EmptyState } from '../../../ui';
import './telephony.scss';

interface Country {
  iso: string;
  name: string;
}

/**
 * Telephony → Countries: read-only view of exactly which countries the
 * backend will actually place a call to today (GET /v1/countries mirrors
 * clickToCall()'s own check) — so an agent can check before dialing instead
 * of finding out from a "not enabled" error after the fact.
 */
const TelephonyCountriesPage: React.FC = () => {
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    telephonyApi<Country[]>('/v1/countries')
      .then(setCountries)
      .catch((e) => toast.error(`Countries: ${e.message}`))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  return (
    <Page>
      <PageHeader
        eyebrow="Telephony"
        title="Countries"
        subtitle={loading ? 'Loading countries…' : `${countries.length} countries enabled for calling`}
        actions={
          <Button variant="secondary" icon={<RotateCcw size={16} aria-hidden />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        }
      />
      <Card
        flush
        title="Supported countries"
        subtitle="Calls to a customer outside this list are rejected before dialling, with a clear reason — this is the same list the backend checks, not a separate copy that could go stale. To enable a new country, contact ops."
      >
        {loading ? (
          <Loader label="Loading countries…" />
        ) : countries.length === 0 ? (
          <div className="tel-flush-pad">
            <EmptyState title="No countries returned" description="Refresh, or contact ops if this persists." />
          </div>
        ) : (
          <Table caption="Countries the backend will place calls to">
            <thead>
              <tr>
                <th>Country</th>
                <th>Code</th>
              </tr>
            </thead>
            <tbody>
              {countries.map((c) => (
                <tr key={c.iso}>
                  <td>{c.name}</td>
                  <td><code>{c.iso}</code></td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Page>
  );
};

export default TelephonyCountriesPage;
